import React, { useEffect, useState } from 'react';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../images/icons/update_sw_success.svg';
import { ReactComponent as FailedToAddUserIcon } from '../../../../images/icons/adding_user_failed.svg';
import { ReactComponent as FailedToAddFacilityIcon } from '../../../../images/icons/facility_add_failed.svg';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  CreateLocationData,
  CreateParentFacilityData,
} from '../../../../store/facility/facilityTypes';
import {
  createLocation,
  setAddParentFacilityAdminStatus,
  createFacilityInvite,
} from '../../../../store/facility/actionCreators';
import { Box, CircularProgress } from '@mui/material';
import { FacilityDetailsForm } from '../../../Dashboard/AddNewController/components/FacilityForm/FacilityDetailsForm';
import { AdminConfirmation } from '../../../Dashboard/AddNewController/components/FacilityForm/AdminConfirmation';
import { AdminTypeSelection } from '../../../Dashboard/AddNewController/components/FacilityForm/AdminTypeSelection';
import { AdminInviteForm } from '../../../Dashboard/AddNewController/components/FacilityForm/AdminInviteForm';

const STEPS = {
  LOCATION_DETAILS: 'location_details',
  ADMIN_CONFIRMATION: 'admin_confirmation',
  ADMIN_TYPE_SELECTION: 'admin_type_selection',
  ADMIN_INVITE: 'admin_invite',
  SUCCESS: 'success',
  ERROR: 'error',
};

const AddLocationController = ({
  parentFacilityId,
  onBack,
  onCancel,
  onSuccess,
}: {
  parentFacilityId: number;
  onBack: () => void;
  onCancel: () => void;
  onSuccess?: () => void;
}) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(STEPS.LOCATION_DETAILS);
  const [locationId, setLocationId] = useState<number | null>(null);
  const { addParentFacilityAdminStatus, isLoading } = useSelector(
    (state: RootState) => state.facility,
  );

  const handleLocationSubmit = async (values: CreateLocationData) => {
    try {
      const payload = {
        ...values,
        phone: values.phone.replace(/\D/g, ''),
        parent_facility_id: parentFacilityId?.toString(),
        country: 'United States', // Currently hardcoded to US
      };
      const location = (await dispatch(createLocation(payload))) as any;
      const newLocationId = location?.id;
      if (newLocationId) {
        setLocationId(newLocationId);
        setCurrentStep(STEPS.ADMIN_CONFIRMATION);
      } else {
        setCurrentStep(STEPS.ERROR);
      }
    } catch (error) {
      setCurrentStep(STEPS.ERROR);
    }
  };

  const handleAdminConfirmation = (wantAdmin: boolean) => {
    if (wantAdmin) {
      setCurrentStep(STEPS.ADMIN_TYPE_SELECTION);
    } else {
      setLocationId(null);
      setCurrentStep(STEPS.SUCCESS);
    }
  };

  const handleAdminTypeSelection = (isNewAdmin: boolean) => {
    if (isNewAdmin) {
      setCurrentStep(STEPS.ADMIN_INVITE);
    }
  };

  const handleAdminInvite = (values: {
    first_name: string;
    last_name: string;
    email: string;
  }) => {
    if (!locationId) return;
    dispatch(
      createFacilityInvite(locationId, {
        ...values,
        facility_role_id: 1,
      }),
    );
  };

  useEffect(() => {
    if (addParentFacilityAdminStatus !== null) {
      setCurrentStep(
        addParentFacilityAdminStatus ? STEPS.SUCCESS : STEPS.ERROR,
      );
    }
  }, [addParentFacilityAdminStatus, dispatch]);
  useEffect(() => {
    dispatch(setAddParentFacilityAdminStatus(null));
  }, [dispatch]);
  const renderStep = () => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      );
    }
    switch (currentStep) {
      case STEPS.LOCATION_DETAILS:
        return (
          <FacilityDetailsForm
            onSubmit={
              handleLocationSubmit as (
                values: CreateLocationData | CreateParentFacilityData,
              ) => void
            }
            onCancel={onBack}
            mode="location"
          />
        );
      case STEPS.ADMIN_CONFIRMATION:
        return (
          <AdminConfirmation
            onNext={handleAdminConfirmation}
            onBack={onCancel}
            mode="location"
          />
        );
      case STEPS.ADMIN_TYPE_SELECTION:
        return (
          <AdminTypeSelection
            onNext={handleAdminTypeSelection}
            onBack={() => setCurrentStep(STEPS.ADMIN_CONFIRMATION)}
            mode="location"
          />
        );
      case STEPS.ADMIN_INVITE:
        return (
          <AdminInviteForm
            actionType="add"
            submitLabel="Add"
            currentStep={2}
            onSubmit={handleAdminInvite}
            onBack={() => setCurrentStep(STEPS.ADMIN_TYPE_SELECTION)}
            mode="location"
          />
        );
      case STEPS.SUCCESS:
        return (
          <StatusMessage
            icon={<UpdateSwSuccessIcon />}
            title="Added successfully!"
            onClose={() => {
              dispatch(setAddParentFacilityAdminStatus(null));
              if (onSuccess) {
                onSuccess();
              } else {
                onCancel();
              }
            }}
          />
        );
      case STEPS.ERROR:
        return (
          <StatusMessage
            icon={
              addParentFacilityAdminStatus === false ? (
                <FailedToAddUserIcon />
              ) : (
                <FailedToAddFacilityIcon />
              )
            }
            title={
              addParentFacilityAdminStatus === false
                ? 'Location admin didn’t save'
                : 'Facility location didn’t save'
            }
            message="Please try again or come back later."
            onClose={() => {
              if (addParentFacilityAdminStatus === false) {
                setCurrentStep(STEPS.ADMIN_INVITE);
                dispatch(setAddParentFacilityAdminStatus(null));
              } else {
                setCurrentStep(STEPS.LOCATION_DETAILS);
              }
            }}
            primaryButtonText="Try again"
          />
        );
      default:
        return null;
    }
  };

  return renderStep();
};

export default AddLocationController;
